"use client";

// Business360 Academy browser — v2.0
// Course-grade catalog: poster-thumbnail cards, modal player (prev/next +
// keyboard), watched + resume progress (localStorage), autoplay-next,
// shareable deep-links (?lesson=<code>), track + level filters + search.
// Shared by /dashboard/education (in-app) and /academy (public). Videos and
// posters are public at /tutorials/<code>.mp4 and /tutorials/thumbs/<code>.jpg.

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  GraduationCap, Clock, Search, BookOpen, X, ChevronLeft, ChevronRight, Check, Play, Link2, Bookmark, Award,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation, type TranslationKey } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { apiGet, apiPatch } from "@/lib/api";
import { PRIMARY, PRIMARY_DARK, SURFACE } from "@/lib/brandColors";

export const ACADEMY_VERSION = "4.0";

type Level = "Beginner" | "Intermediate" | "Advanced";
type Chapter = { code: string; name: string; len: string };
type Lesson = { code: string; name: string; len: string; level: Level; desc: string; video?: string; chapters?: Chapter[] };
type Track = { title: string; subtitle: string; lessons: Lesson[] };

const LEVEL_TONE: Record<Level, string> = {
  Beginner:     "text-success bg-success/10",
  Intermediate: "text-info bg-info/10",
  Advanced:     "text-warning bg-warning/10",
};

const TRACKS: Track[] = [
  {
    title: "Getting Started",
    subtitle: "Everyone watches these first",
    lessons: [
      { code: "P1", name: "Getting Started",         len: "5 min", level: "Beginner", desc: "Register, log in, reset your password, and run the first-time setup wizard.", video: "/tutorials/01-welcome-tour.mp4" },
      { code: "P2", name: "The Dashboard & Navigation", len: "5 min", level: "Beginner", desc: "Home dashboard, sidebar, header bar, command palette (⌘K), and the trial banner." },
      { code: "P3", name: "App Store & Your Modules", len: "5 min", level: "Beginner", desc: "Browse, install and uninstall modules, plus the community marketplace." },
      { code: "P4", name: "Account, Team & Settings", len: "6 min", level: "Beginner", desc: "Account & password, organization info, team members, roles, and billing." },
    ],
  },
  {
    title: "Finance",
    subtitle: "Books, invoicing and reporting",
    lessons: [
      { code: "01", name: "Accounting",       len: "5 videos", level: "Intermediate", desc: "A deep-dive: build the chart of accounts, add vendors & bills, raise invoices, and read the balance sheet & cash flow — real click-by-click.", chapters: [
        { code: "01.1", name: "Accounting — Overview",        len: "0:37" },
        { code: "01.2", name: "Build your chart of accounts", len: "0:43" },
        { code: "01.3", name: "Vendors & bills",              len: "0:26" },
        { code: "01.4", name: "Invoices & getting paid",      len: "0:23" },
        { code: "01.5", name: "Financial statements",         len: "0:27" },
      ] },
      { code: "02", name: "Invoicing",        len: "5 min", level: "Beginner",     desc: "Create an invoice with line items, send it, and track paid vs unpaid." },
      { code: "03", name: "Expenses",         len: "4 min", level: "Beginner",     desc: "Log an expense claim, walk it through approval, and report it out." },
      { code: "04", name: "Multi-Currency",   len: "4 min", level: "Intermediate", desc: "Enable currencies, set exchange rates, and convert across companies." },
      { code: "05", name: "Reports",          len: "4 min", level: "Beginner",     desc: "KPI overview, monthly P&L, alerts, and PDF export." },
      { code: "06", name: "Business Intelligence", len: "5 videos", level: "Advanced", desc: "A deep-dive: build a dashboard, slice any module's data in the report builder, read forecasts & insights, and ask the AI — real click-by-click.", chapters: [
        { code: "06.1", name: "Business Intelligence — Overview", len: "0:30" },
        { code: "06.2", name: "Build a dashboard",               len: "0:24" },
        { code: "06.3", name: "The report builder",              len: "0:23" },
        { code: "06.4", name: "Forecasts & insights",            len: "0:24" },
        { code: "06.5", name: "Ask the AI & autopilot",          len: "0:27" },
      ] },
    ],
  },
  {
    title: "Sales & Commerce",
    subtitle: "Turning interest into revenue",
    lessons: [
      { code: "07", name: "CRM", len: "7 videos", level: "Intermediate", desc: "A full deep-dive: capture & AI-score leads, convert to customers, build pipelines, work deals, and manage tasks — real click-by-click.", chapters: [
        { code: "07.1", name: "CRM — Overview",              len: "0:36" },
        { code: "07.2", name: "Capture & AI-score a lead",   len: "1:04" },
        { code: "07.3", name: "Find & filter your leads",    len: "0:22" },
        { code: "07.4", name: "Convert a lead to a customer",len: "0:28" },
        { code: "07.5", name: "Build your sales pipeline",   len: "0:40" },
        { code: "07.6", name: "Work deals on the kanban",    len: "0:32" },
        { code: "07.7", name: "Never drop a follow-up: Tasks",len: "0:42" },
      ] },
      { code: "08", name: "Sales",          len: "5 videos", level: "Intermediate", desc: "A deep-dive: build a quote in the full builder, draft with AI & templates, route for approval & e-sign, and read sales analytics — real click-by-click.", chapters: [
        { code: "08.1", name: "Sales — Overview",        len: "0:32" },
        { code: "08.2", name: "Build a quote",           len: "0:35" },
        { code: "08.3", name: "AI drafting & templates", len: "0:25" },
        { code: "08.4", name: "Approval & e-sign",       len: "0:22" },
        { code: "08.5", name: "Orders & analytics",      len: "0:26" },
      ] },
      { code: "09", name: "Contacts",       len: "5 min", level: "Beginner",     desc: "Companies and the people inside them — one shared B2B address book." },
      { code: "10", name: "POS Shop",       len: "5 videos", level: "Intermediate", desc: "A deep-dive: ring up a counter order, manage your catalog, track deliveries, and meet AI reception — real click-by-click.", chapters: [
        { code: "10.1", name: "POS Shop — Overview",   len: "0:33" },
        { code: "10.2", name: "Take a counter order",  len: "0:46" },
        { code: "10.3", name: "The catalog",           len: "0:20" },
        { code: "10.4", name: "Deliveries",            len: "0:19" },
        { code: "10.5", name: "AI reception",          len: "0:28" },
      ] },
      { code: "11", name: "Restaurant POS", len: "6 min", level: "Intermediate", desc: "Floor & tables, build a ticket, send to kitchen, and settle." },
      { code: "12", name: "eCommerce",      len: "6 min", level: "Intermediate", desc: "Publish products, the storefront, and managing online orders." },
      { code: "13", name: "Subscriptions",  len: "5 min", level: "Intermediate", desc: "Plans, subscribe a customer, lifecycle, MRR and churn." },
      { code: "14", name: "Rental",         len: "5 min", level: "Intermediate", desc: "Rentable items, orders through reserved → out → returned → overdue." },
    ],
  },
  {
    title: "Supply Chain",
    subtitle: "Stock, procurement and production",
    lessons: [
      { code: "15", name: "Inventory", len: "5 videos", level: "Intermediate", desc: "A deep-dive: add products, organize warehouses & bins, set reorder points, and read stock insights — real click-by-click.", chapters: [
        { code: "15.1", name: "Inventory — Overview",     len: "0:43" },
        { code: "15.2", name: "Add a product",            len: "0:44" },
        { code: "15.3", name: "Warehouses & locations",   len: "0:30" },
        { code: "15.4", name: "Reorder & low-stock",      len: "0:27" },
        { code: "15.5", name: "Insights",                 len: "0:24" },
      ] },
      { code: "16", name: "Purchase",      len: "5 videos", level: "Intermediate", desc: "A deep-dive: raise a requisition, compare quotes with RFQs, approve purchase orders, and match vendor bills — the whole procurement flow, real click-by-click.", chapters: [
        { code: "16.1", name: "Purchase — Overview",           len: "0:32" },
        { code: "16.2", name: "Raise a requisition",           len: "0:27" },
        { code: "16.3", name: "RFQs & comparing quotes",       len: "0:20" },
        { code: "16.4", name: "Purchase orders & approvals",   len: "0:19" },
        { code: "16.5", name: "Receiving & vendor bills",      len: "0:27" },
      ] },
      { code: "17", name: "Manufacturing", len: "6 min", level: "Intermediate", desc: "Bills of materials, work orders, and tracking a build." },
      { code: "18", name: "O&M",           len: "7 min", level: "Intermediate", desc: "Assets, preventive maintenance, work orders, spare parts and calendar." },
      { code: "19", name: "PLM",           len: "5 min", level: "Intermediate", desc: "BOM versions, engineering change orders, and revision history." },
      { code: "20", name: "Quality",       len: "5 min", level: "Intermediate", desc: "Quality checks, inspections, and non-conformance reports." },
      { code: "21", name: "Planning",      len: "4 min", level: "Intermediate", desc: "Schedule and assign work with capacity at a glance." },
    ],
  },
  {
    title: "HR & People",
    subtitle: "Hire, manage and develop your team",
    lessons: [
      { code: "22", name: "HR", len: "5 videos", level: "Intermediate", desc: "A deep-dive: add employees, track attendance, run leave requests & approvals, and read the org chart — real click-by-click.", chapters: [
        { code: "22.1", name: "HR — Overview",              len: "0:30" },
        { code: "22.2", name: "Add an employee",            len: "0:35" },
        { code: "22.3", name: "Attendance at a glance",     len: "0:17" },
        { code: "22.4", name: "Leave requests & approvals", len: "0:22" },
        { code: "22.5", name: "The org chart",              len: "0:19" },
      ] },
      { code: "23", name: "Recruitment",  len: "5 min", level: "Intermediate", desc: "Post a job and move candidates through the hiring pipeline." },
      { code: "24", name: "Time Off",     len: "4 min", level: "Beginner",     desc: "Leave types, requests, approvals and balances." },
      { code: "25", name: "Timesheets",   len: "4 min", level: "Beginner",     desc: "Log billable time, submit, approve, and feed reports." },
      { code: "26", name: "Appraisals",   len: "5 min", level: "Intermediate", desc: "Open a review cycle, assign reviews, and record ratings." },
      { code: "27", name: "Onboarding",   len: "4 min", level: "Beginner",     desc: "Import your data and run the guided setup tour." },
    ],
  },
  {
    title: "Marketing & Communication",
    subtitle: "Reach and engage your audience",
    lessons: [
      { code: "28", name: "Email Marketing",       len: "6 min", level: "Intermediate", desc: "Build an audience, compose a campaign, send and track opens." },
      { code: "29", name: "SMS Marketing",         len: "6 min", level: "Intermediate", desc: "Packages, recipient lists, and sending an SMS campaign." },
      { code: "30", name: "Social Marketing",      len: "5 min", level: "Intermediate", desc: "Connect accounts, compose, and schedule posts across channels." },
      { code: "31", name: "Marketing Automation",  len: "6 min", level: "Advanced",     desc: "Build a journey — trigger, wait, email/SMS, branch — and activate it." },
      { code: "32", name: "WhatsApp (META)",       len: "7 min", level: "Intermediate", desc: "Connect the number, templates, shared inbox, and broadcasts." },
      { code: "33", name: "Referrals",             len: "4 min", level: "Intermediate", desc: "Set up a referral program, share links, and track payouts." },
      { code: "34", name: "Events",                len: "5 min", level: "Intermediate", desc: "Create an event, take registrations, and manage attendees." },
      { code: "35", name: "Blog",                  len: "4 min", level: "Beginner",     desc: "Write a post with SEO fields and publish the public blog." },
      { code: "36", name: "Forum",                 len: "4 min", level: "Beginner",     desc: "Create topics, start discussions, and moderate a community." },
      { code: "37", name: "Website",               len: "6 min", level: "Intermediate", desc: "Build pages, set domain & SEO, and publish your site." },
    ],
  },
  {
    title: "Services & Operations",
    subtitle: "Deliver work and support customers",
    lessons: [
      { code: "38", name: "Projects",      len: "5 videos", level: "Intermediate", desc: "A deep-dive: create a project, plan tasks & milestones, balance team workload, handle tickets and read analytics — real click-by-click.", chapters: [
        { code: "38.1", name: "Projects — Overview",   len: "0:34" },
        { code: "38.2", name: "Create a project",      len: "0:30" },
        { code: "38.3", name: "Planning & tasks",      len: "0:19" },
        { code: "38.4", name: "Team workload",         len: "0:19" },
        { code: "38.5", name: "Tickets & analytics",   len: "0:27" },
      ] },
      { code: "39", name: "Field Service", len: "5 min", level: "Intermediate", desc: "Create a job, dispatch a technician, and complete it in the field." },
      { code: "40", name: "Helpdesk",      len: "5 min", level: "Intermediate", desc: "Triage a ticket by priority & SLA, reply, and resolve." },
      { code: "41", name: "Live Chat",     len: "5 min", level: "Intermediate", desc: "Install the widget, work the agent inbox, and use canned replies." },
      { code: "42", name: "eLearning",     len: "5 min", level: "Intermediate", desc: "Build a course, enroll learners, and track completion." },
      { code: "43", name: "Surveys",       len: "4 min", level: "Intermediate", desc: "Build a survey, share it, and analyze responses." },
      { code: "44", name: "Calendar",      len: "4 min", level: "Beginner",     desc: "Create events and see dates from across every module in one place." },
      { code: "45", name: "Knowledge",     len: "4 min", level: "Beginner",     desc: "Write articles, organize, and search your internal wiki." },
      { code: "46", name: "Sign",          len: "5 min", level: "Intermediate", desc: "Add signature fields, send for e-signature, and track status." },
    ],
  },
  {
    title: "Productivity & Integration",
    subtitle: "Automate, extend and connect",
    lessons: [
      { code: "47", name: "Workflows",     len: "5 videos", level: "Advanced",     desc: "A deep-dive: build an automation in the visual builder, wire triggers, conditions and actions, and read run logs — real click-by-click.", chapters: [
        { code: "47.1", name: "Workflows — Overview",     len: "0:33" },
        { code: "47.2", name: "Build a workflow",         len: "0:26" },
        { code: "47.3", name: "Triggers & conditions",    len: "0:24" },
        { code: "47.4", name: "Actions",                  len: "0:23" },
        { code: "47.5", name: "Templates & run logs",     len: "0:27" },
      ] },
      { code: "48", name: "Custom Fields", len: "5 min", level: "Intermediate", desc: "Add fields to any entity and build custom views." },
      { code: "49", name: "Audit Trail",   len: "4 min", level: "Intermediate", desc: "Browse the audit log and the compliance view." },
      { code: "50", name: "Collaboration", len: "4 min", level: "Beginner",     desc: "Comments, mentions, and the shared activity feed." },
      { code: "51", name: "API Hub",       len: "6 min", level: "Advanced",     desc: "API keys, the docs explorer, and registering webhooks." },
      { code: "52", name: "Document Manager", len: "5 min", level: "Intermediate", desc: "Upload & organize documents, templates, and versions." },
      { code: "53", name: "LINKS",         len: "6 min", level: "Intermediate", desc: "Cloud telephony — set up a line, make & log calls, and routing." },
      { code: "54", name: "IoT",           len: "5 min", level: "Advanced",     desc: "Register a device, live telemetry, and threshold alerts." },
      { code: "55", name: "Fleet",         len: "4 min", level: "Intermediate", desc: "Vehicles, maintenance schedules, and running costs." },
      { code: "56", name: "Water Treatment", len: "8 min", level: "Advanced",   desc: "Design a plant — equipment library, projection engine, costs and reports." },
      { code: "57", name: "AI Copilot",    len: "6 min", level: "Intermediate", desc: "Ask the copilot in plain English and use the recipe library." },
    ],
  },
  // G1 — the contracting track. Unlike every other track above, these three are
  // ONE story followed in order: the tender you price in 58 becomes the budget
  // you control in 59 and the certificate you get paid on in 60. The subtitle
  // says so, because a contractor who watches 60 first will not understand
  // where "previously certified" came from.
  //
  // No `video:` and no entry in VIDEO_CODES — the scripts are written
  // (docs/tutorial-scripts/58–60) but the recordings have not been produced.
  // A lesson with a dead video link is worse than one that is plainly text.
  {
    title: "Construction & Contracting",
    subtitle: "One job, followed end to end — watch these in order",
    lessons: [
      { code: "58", name: "Estimating",       len: "8 min", level: "Intermediate", desc: "Register a tender, import a BOQ, build rates from labour, material and plant, send RFQs — and hand a won tender over as a project budget." },
      { code: "59", name: "Construction",     len: "9 min", level: "Intermediate", desc: "Cost codes and WBS, a baselined budget, the four-column cost report, progress measurement with earned value, the site diary, and awarding a subcontract on a bid comparison." },
      { code: "60", name: "Contract Billing", len: "9 min", level: "Advanced",     desc: "Main contract terms, variation orders that move the sum, the interim payment certificate cascade, the approval lifecycle, and the retention ledger." },
    ],
  },
];

const LEVELS: Array<"All" | Level> = ["All", "Beginner", "Intermediate", "Advanced"];
// Chrome is translated; lesson names and descriptions stay English because
// they name English-narrated videos. Track titles double as filter keys, so
// the English title stays the identity and only the label is looked up.
const LEVEL_I18N: Record<"All" | Level, TranslationKey> = {
  All: "academy.levelAll", Beginner: "academy.levelBeginner",
  Intermediate: "academy.levelIntermediate", Advanced: "academy.levelAdvanced",
};
const TRACK_I18N: Record<string, { title: TranslationKey; sub: TranslationKey }> = {
  "Getting Started":            { title: "academy.trackGettingStarted", sub: "academy.trackGettingStartedSub" },
  "Finance":                    { title: "academy.trackFinance",        sub: "academy.trackFinanceSub" },
  "Sales & Commerce":           { title: "academy.trackSales",          sub: "academy.trackSalesSub" },
  "Supply Chain":               { title: "academy.trackSupplyChain",    sub: "academy.trackSupplyChainSub" },
  "HR & People":                { title: "academy.trackHr",             sub: "academy.trackHrSub" },
  "Marketing & Communication":  { title: "academy.trackMarketing",      sub: "academy.trackMarketingSub" },
  "Services & Operations":      { title: "academy.trackServices",       sub: "academy.trackServicesSub" },
  "Productivity & Integration": { title: "academy.trackProductivity",   sub: "academy.trackProductivitySub" },
  "Construction & Contracting": { title: "academy.trackConstruction",   sub: "academy.trackConstructionSub" },
};
const TRACK_TITLES = TRACKS.map((t) => t.title);
const VIDEO_CODES = new Set<string>([
  "P2", "P3", "P4",
  ...Array.from({ length: 57 }, (_, i) => String(i + 1).padStart(2, "0")),
]);
/**
 * Academy progress has two homes, because the Academy has two audiences.
 *
 * /academy is a PUBLIC marketing route, so an anonymous visitor has nowhere but
 * localStorage — which is all they ever had. /dashboard/education is the same
 * component behind auth, and there the server is the truth: localStorage is
 * lost on a cache clear, never follows anyone to a second device, and made the
 * completion certificate forgeable by writing one key from the console.
 *
 * localStorage stays in BOTH cases as the first-paint cache. The server load is
 * a round trip; painting an empty Academy for a moment and then filling it in
 * looks like data loss to someone who watched twenty lessons.
 */
const WATCHED_KEY = "b360:academy:watched";
const PROGRESS_KEY = "b360:academy:progress";
const SAVED_KEY = "b360:academy:saved";

type FlatLesson = Lesson & { track: string };
const FLAT: FlatLesson[] = TRACKS.flatMap((t) => t.lessons.map((l) => ({ ...l, track: t.title })));
const TOTAL = FLAT.length;

type Prog = { t: number; d: number; at: number };

const videoUrl = (l: Lesson): string | undefined =>
  l.video ?? (VIDEO_CODES.has(l.code) ? `/tutorials/${l.code}.mp4` : undefined);
const posterUrl = (l: Lesson): string => `/tutorials/thumbs/${l.code}.jpg`;

type ServerRow = { code: string; status: string; positionSec: number; saved: boolean };

export function AcademyBrowser() {
  const { t } = useTranslation();
  const levelLabel = (lv: "All" | Level) => t(LEVEL_I18N[lv], lv);
  const trackTitle = (title: string) => { const k = TRACK_I18N[title]; return k ? t(k.title, title) : title; };
  const trackSub = (title: string, sub: string) => { const k = TRACK_I18N[title]; return k ? t(k.sub, sub) : sub; };
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<"All" | Level>("All");
  const [trackFilter, setTrackFilter] = useState<"All" | string>("All");
  const [watched, setWatched] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<Record<string, Prog>>({});
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [savedOnly, setSavedOnly] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [name, setName] = useState("");
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [course, setCourse] = useState<{ lessonCode: string; ch: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSaveRef = useRef(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // ── hydrate persisted state ──
  useEffect(() => {
    try {
      const w = localStorage.getItem(WATCHED_KEY);
      if (w) setWatched(new Set(JSON.parse(w) as string[]));
      const p = localStorage.getItem(PROGRESS_KEY);
      if (p) setProgress(JSON.parse(p) as Record<string, Prog>);
      const sv = localStorage.getItem(SAVED_KEY);
      if (sv) setSaved(new Set(JSON.parse(sv) as string[]));
      const nm = localStorage.getItem("b360:academy:name");
      if (nm) setName(nm);
    } catch { /* ignore */ }
    // deep-link: ?lesson=<code>
    try {
      const code = new URLSearchParams(window.location.search).get("lesson");
      if (code) {
        const lc = FLAT.find((x) => x.code === code && x.chapters && x.chapters.length);
        if (lc) { setCourse({ lessonCode: lc.code, ch: 0 }); }
        else {
          let hit = false;
          for (const l of FLAT) { if (l.chapters) { const ci = l.chapters.findIndex((c) => c.code === code); if (ci >= 0) { setCourse({ lessonCode: l.code, ch: ci }); hit = true; break; } } }
          if (!hit) { const i = FLAT.findIndex((x) => x.code === code); if (i >= 0) setActiveIdx(i); }
        }
      }
    } catch { /* ignore */ }
  }, []);

  // ── the server's copy, for a signed-in viewer ──
  // Runs after the local hydrate above and OVERWRITES it: localStorage is the
  // first-paint cache, the server is the truth. Merged rather than replaced for
  // `watched`, so lessons finished offline on this device are not thrown away
  // by a load that has not seen them yet — the next write pushes them up.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    void apiGet<{ progress: ServerRow[] }>("/api/academy/progress")
      .then(({ progress: rows }) => {
        if (cancelled || !Array.isArray(rows)) return;
        setWatched((prev) => {
          const n = new Set(prev);
          for (const r of rows) if (r.status === "done") n.add(r.code);
          return n;
        });
        setSaved((prev) => {
          const n = new Set(prev);
          for (const r of rows) { if (r.saved) n.add(r.code); else n.delete(r.code); }
          return n;
        });
        setProgress((prev) => {
          const n = { ...prev };
          for (const r of rows) {
            if (r.status === "done") { delete n[r.code]; continue; }
            // Duration is not stored server-side — it is a property of the file,
            // not of the person. Keep whatever this device already knew.
            if (r.positionSec > 0) n[r.code] = { t: r.positionSec, d: prev[r.code]?.d ?? 0, at: Date.now() };
          }
          return n;
        });
      })
      // Signed in but the call failed (offline, tenant mid-migration): the local
      // cache is still on screen and still usable. Nothing to say about it.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [signedIn]);

  // "/" focuses search (unless typing or a modal is open)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (activeIdx != null || certOpen) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [activeIdx, certOpen]);

  const persistWatched = useCallback((s: Set<string>) => {
    try { localStorage.setItem(WATCHED_KEY, JSON.stringify([...s])); } catch { /* ignore */ }
  }, []);
  /**
   * Mirror one lesson's state to the server. Fire-and-forget on purpose: losing
   * a watch marker must never interrupt playback, and the next write re-states
   * the whole row anyway. Silently a no-op when signed out.
   */
  const sync = useCallback((code: string, patch: {
    status?: "in_progress" | "done"; positionSec?: number; saved?: boolean;
  }) => {
    if (!signedIn) return;
    void apiPatch("/api/academy/progress", { code, ...patch }).catch(() => {});
  }, [signedIn]);

  const toggleWatched = useCallback((code: string) => {
    setWatched((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code); else n.add(code);
      persistWatched(n);
      sync(code, { status: n.has(code) ? "done" : "in_progress", positionSec: 0 });
      return n;
    });
  }, [persistWatched, sync]);
  const markWatched = useCallback((code: string) => {
    setWatched((prev) => {
      if (prev.has(code)) return prev;
      const n = new Set(prev); n.add(code); persistWatched(n);
      sync(code, { status: "done", positionSec: 0 });
      return n;
    });
  }, [persistWatched, sync]);

  const saveProgress = useCallback((code: string, t: number, d: number) => {
    setProgress((prev) => { const n = { ...prev, [code]: { t, d, at: Date.now() } }; try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(n)); } catch { /* ignore */ } return n; });
    // Already throttled by the caller's 4s save gate, so this is not chatty.
    sync(code, { status: "in_progress", positionSec: Math.floor(t) });
  }, [sync]);
  const clearProgress = useCallback((code: string) => {
    setProgress((prev) => { if (!prev[code]) return prev; const n = { ...prev }; delete n[code]; try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(n)); } catch { /* ignore */ } return n; });
  }, []);
  const toggleSaved = useCallback((code: string) => {
    setSaved((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code); else n.add(code);
      try { localStorage.setItem(SAVED_KEY, JSON.stringify([...n])); } catch { /* ignore */ }
      sync(code, { saved: n.has(code) });
      return n;
    });
  }, [sync]);

  const trackStats = useMemo(() =>
    Object.fromEntries(TRACKS.map((t) => [t.title, {
      done: t.lessons.filter((l) => watched.has(l.code)).length,
      total: t.lessons.length,
    }])), [watched]);

  const tracks = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TRACKS
      .filter((tr) => trackFilter === "All" || tr.title === trackFilter)
      .map((tr) => ({
        ...tr,
        lessons: tr.lessons.filter(
          (l) =>
            (level === "All" || l.level === level) &&
            (!savedOnly || saved.has(l.code)) &&
            (!q || l.name.toLowerCase().includes(q) || l.desc.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)),
        ),
      }))
      .filter((tr) => tr.lessons.length > 0);
  }, [query, level, trackFilter, savedOnly, saved]);
  const shownCount = tracks.reduce((n, t) => n + t.lessons.length, 0);

  const openLesson = useCallback((code: string) => {
    setCopied(false);
    const l = FLAT.find((x) => x.code === code);
    if (l?.chapters && l.chapters.length) {
      const fu = l.chapters.findIndex((c) => !watched.has(c.code));
      setCourse({ lessonCode: l.code, ch: fu < 0 ? 0 : fu });
      return;
    }
    const i = FLAT.findIndex((x) => x.code === code);
    if (i >= 0) setActiveIdx(i);
  }, [watched]);
  const goCh = useCallback((dir: number) => {
    setCourse((c) => {
      if (!c) return c;
      const l = FLAT.find((x) => x.code === c.lessonCode);
      const n = l?.chapters?.length ?? 0;
      return { ...c, ch: Math.min(n - 1, Math.max(0, c.ch + dir)) };
    });
  }, []);
  const close = useCallback(() => { setActiveIdx(null); setCourse(null); }, []);
  const go = useCallback((dir: number) => {
    setCopied(false);
    setActiveIdx((i) => (i == null ? i : Math.min(FLAT.length - 1, Math.max(0, i + dir))));
  }, []);

  const active = activeIdx != null ? FLAT[activeIdx] : null;
  const courseL = course ? FLAT.find((l) => l.code === course.lessonCode) ?? null : null;
  const courseChs = courseL?.chapters ?? [];
  const activeCh = course && courseChs.length ? courseChs[course.ch] : undefined;

  // ── modal: keyboard, scroll lock, URL deep-link sync (single lesson OR course chapter) ──
  useEffect(() => {
    const single = activeIdx != null ? FLAT[activeIdx] : null;
    const courseL = course ? FLAT.find((l) => l.code === course.lessonCode) : null;
    const chCode = courseL?.chapters?.[course ? course.ch : 0]?.code;
    const openCode = single?.code ?? chCode ?? null;
    try {
      const u = new URL(window.location.href);
      if (openCode) u.searchParams.set("lesson", openCode); else u.searchParams.delete("lesson");
      window.history.replaceState(null, "", u.toString());
    } catch { /* ignore */ }
    if (!single && !course) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (single && e.key === "ArrowRight") go(1);
      else if (single && e.key === "ArrowLeft") go(-1);
      else if (course && e.key === "ArrowRight") goCh(1);
      else if (course && e.key === "ArrowLeft") goCh(-1);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [activeIdx, course, close, go, goCh]);

  // ── resume: pick most-recent in-progress, else first unwatched ──
  const resume = useMemo(() => {
    const cands = FLAT.filter((l) => { const p = progress[l.code]; return p && !watched.has(l.code) && p.d > 0 && p.t < p.d * 0.95 && p.t > 3; });
    if (cands.length) return cands.reduce((a, b) => (progress[b.code].at > progress[a.code].at ? b : a));
    return FLAT.find((l) => !watched.has(l.code)) ?? FLAT[0];
  }, [progress, watched]);
  const resuming = !!(progress[resume.code] && !watched.has(resume.code) && progress[resume.code].t > 3);

  // Continue-watching shelf: in-progress lessons, most recent first
  const continueList = useMemo(() => FLAT
    .filter((l) => { const p = progress[l.code]; return p && !watched.has(l.code) && p.d > 0 && p.t > 3 && p.t < p.d * 0.95; })
    .sort((a, b) => progress[b.code].at - progress[a.code].at)
    .slice(0, 8), [progress, watched]);
  const savedList = useMemo(() => FLAT.filter((l) => saved.has(l.code)), [saved]);

  const doneCount = watched.size;
  const pct = Math.round((doneCount / TOTAL) * 100);

  // ── video element handlers ──
  const onLoaded = () => {
    const v = videoRef.current; if (!v || !active) return;
    const p = progress[active.code];
    if (p && p.d > 0 && p.t > 3 && p.t < p.d * 0.95) { try { v.currentTime = p.t; } catch { /* ignore */ } }
  };
  const onTime = () => {
    const v = videoRef.current; if (!v || !active || !v.duration) return;
    const now = Date.now();
    if (now - lastSaveRef.current > 4000) { lastSaveRef.current = now; saveProgress(active.code, v.currentTime, v.duration); }
  };
  const onPause = () => { const v = videoRef.current; if (v && active && v.duration && !v.ended) saveProgress(active.code, v.currentTime, v.duration); };
  const onEnded = () => {
    if (!active) return;
    markWatched(active.code); clearProgress(active.code);
    if (activeIdx != null && activeIdx < FLAT.length - 1) go(1);
  };
  const copyLink = async () => {
    if (!active) return;
    try {
      const u = new URL(window.location.href); u.searchParams.set("lesson", active.code);
      await navigator.clipboard.writeText(u.toString());
      setCopied(true); window.setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };
  const printCertificate = async () => {
    if (watched.size < TOTAL) return;
    // For a signed-in viewer, re-check completion against the SERVER before
    // printing. The local count is a cache, and seeding it was all it ever took
    // to forge a certificate — _cert-shot.spec.ts does exactly that on purpose.
    // An anonymous visitor on the public /academy has no server state to check
    // and keeps the local behaviour; their certificate attests to nothing but
    // their own browser, which is all it ever did.
    if (signedIn) {
      try {
        const { progress: rows } = await apiGet<{ progress: ServerRow[] }>("/api/academy/progress");
        const done = new Set(rows.filter((r) => r.status === "done").map((r) => r.code));
        if (done.size < TOTAL) { setCertOpen(false); return; }
      } catch {
        // Cannot verify — do not print. A certificate that might be unearned is
        // worse than one that is briefly unavailable.
        return;
      }
    }
    const who = (name || "Your Name").replace(/[<>]/g, "");
    const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const w = window.open("", "_blank", "width=1160,height=840");
    if (!w) return;

    const wordmark =
      `<svg viewBox="0 0 460 140" width="248" height="76" role="img" aria-label="Business360">` +
      `<defs><linearGradient id="b360wm" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="${PRIMARY}"/><stop offset="100%" stop-color="${PRIMARY_DARK}"/></linearGradient></defs>` +
      `<g transform="translate(75,70)"><path d="M -45 0 A 45 45 0 1 1 32 32" fill="none" stroke="url(#b360wm)" stroke-width="9" stroke-linecap="round"/><path d="M 25 25 L 38 38 L 25 46" fill="none" stroke="url(#b360wm)" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/><circle cx="-45" cy="0" r="5.5" fill="url(#b360wm)"/></g>` +
      `<text x="160" y="60" font-family="Helvetica,Arial,sans-serif" font-size="30" font-weight="700" letter-spacing="-1.2" fill="${SURFACE.foreground}">BUSINESS</text>` +
      `<text x="160" y="106" font-family="Helvetica,Arial,sans-serif" font-size="46" font-weight="300" letter-spacing="-2.5" fill="url(#b360wm)">360°</text></svg>`;

    const scallops = Array.from({ length: 24 }, (_, i) => {
      const a = i * (Math.PI / 12);
      return `<circle cx="${(60 + Math.cos(a) * 44).toFixed(1)}" cy="${(58 + Math.sin(a) * 44).toFixed(1)}" r="6" fill="url(#gold1)"/>`;
    }).join("");
    const seal =
      `<svg width="120" height="146" viewBox="0 0 120 146" xmlns="http://www.w3.org/2000/svg">` +
      `<defs><linearGradient id="gold1" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f3e09a"/><stop offset=".5" stop-color="#c9a227"/><stop offset="1" stop-color="#9c7614"/></linearGradient>` +
      `<radialGradient id="gold2" cx="36%" cy="30%" r="75%"><stop offset="0" stop-color="#fbf1cf"/><stop offset="1" stop-color="#cba52c"/></radialGradient></defs>` +
      `<path d="M42 118 L34 145 L55 132 L60 116 Z" fill="#b8901f"/><path d="M78 118 L86 145 L65 132 L60 116 Z" fill="#a8801a"/>` +
      scallops +
      `<circle cx="60" cy="58" r="41" fill="url(#gold2)" stroke="#9c7614" stroke-width="1.5"/>` +
      `<circle cx="60" cy="58" r="34" fill="none" stroke="#fff8e1" stroke-width="1.4" opacity=".75"/>` +
      `<g transform="translate(60,50)" fill="#6b4e0e"><path d="M0 -9 L20 0 L0 9 L-20 0 Z"/><path d="M-12 3.5 L-12 13 C-12 17.5 12 17.5 12 13 L12 3.5 L0 8 Z"/><circle cx="20" cy="0" r="1.8"/><path d="M20 0 L20 11" stroke="#6b4e0e" stroke-width="1.5" fill="none"/></g>` +
      `<text x="60" y="83" text-anchor="middle" font-family="Georgia,serif" font-size="9.5" font-weight="700" fill="#6b4e0e" letter-spacing="1.6">ACADEMY</text></svg>`;

    const watermark =
      `<svg viewBox="0 0 128 128" width="440" height="440"><g transform="translate(69,64)"><path d="M -55 0 A 55 55 0 1 1 39 39" fill="none" stroke="#1e2d40" stroke-width="11" stroke-linecap="round"/><path d="M 30 30 L 46 46 L 30 55" fill="none" stroke="#1e2d40" stroke-width="11" stroke-linecap="round" stroke-linejoin="round"/><circle cx="-55" cy="0" r="7" fill="#1e2d40"/></g></svg>`;

    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Business360 Academy — Certificate</title>` +
      `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
      `<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Cormorant+Garamond:wght@500;600&display=swap" rel="stylesheet">` +
      `<style>` +
      `*{box-sizing:border-box;margin:0;padding:0}html,body{height:100%}` +
      `body{font-family:'Cormorant Garamond',Georgia,serif;color:#1e2d40;background:#e9edf2;display:flex;align-items:center;justify-content:center;min-height:100vh;padding:26px}` +
      `.sheet{width:1040px;max-width:97vw;aspect-ratio:1.414/1;background:radial-gradient(120% 120% at 50% 0%,#fffdf7 0%,#fbf6ea 60%,#f6efdd 100%);position:relative;box-shadow:0 24px 60px rgba(20,30,50,.28);overflow:hidden}` +
      `.frame{position:absolute;inset:20px;border:2.5px solid #c9a227}.frame:before{content:'';position:absolute;inset:6px;border:1px solid #c9a22766}` +
      `.corner{position:absolute;width:46px;height:46px;border:0 solid #c9a227}` +
      `.c-tl{top:20px;left:20px;border-top-width:6px;border-left-width:6px}.c-tr{top:20px;right:20px;border-top-width:6px;border-right-width:6px}` +
      `.c-bl{bottom:20px;left:20px;border-bottom-width:6px;border-left-width:6px}.c-br{bottom:20px;right:20px;border-bottom-width:6px;border-right-width:6px}` +
      `.wm{position:absolute;top:53%;left:50%;transform:translate(-50%,-50%);opacity:.045;pointer-events:none}` +
      `.inner{position:relative;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:56px 90px}` +
      `.eyebrow{font-family:'Playfair Display',serif;letter-spacing:.42em;text-transform:uppercase;font-size:12px;color:#9c7614;font-weight:700;margin-top:10px}` +
      `h1{font-family:'Playfair Display',serif;font-weight:800;font-size:44px;color:#1e2d40;margin:12px 0 2px;letter-spacing:.5px}` +
      `.divider{display:flex;align-items:center;gap:12px;color:#c9a227;margin:8px 0 12px}` +
      `.divider .line{width:90px;height:1px;background:linear-gradient(90deg,transparent,#c9a227)}.divider .line.r{background:linear-gradient(90deg,#c9a227,transparent)}` +
      `.pres{font-size:19px;color:#5b667a}` +
      `.name{font-family:'Playfair Display',serif;font-weight:700;font-size:50px;color:#12203a;margin:8px 0 2px;line-height:1.05}` +
      `.nrule{width:340px;height:2px;background:linear-gradient(90deg,transparent,#c9a227,transparent);margin:2px auto 16px}` +
      `.body{font-size:21px;color:#43506a;max-width:660px;line-height:1.5}.body b{color:#1e2d40;font-weight:600}` +
      `.foot{width:100%;max-width:720px;margin-top:6px;display:flex;justify-content:space-between;gap:44px}` +
      `.sig{flex:1;text-align:center}.sig .val{font-family:'Playfair Display',serif;font-size:19px;color:#12203a;padding-bottom:6px}` +
      `.sig .ln{height:1px;background:#1e2d4055;margin-bottom:5px}.sig .lbl{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#8a93a6;font-family:'Playfair Display',serif}` +
      `@media print{@page{size:A4 landscape;margin:0}body{background:#fff;padding:0;min-height:auto}.sheet{box-shadow:none;width:100%;max-width:none;height:100vh;aspect-ratio:auto}}` +
      `</style></head><body><div class="sheet">` +
      `<div class="wm">${watermark}</div>` +
      `<div class="frame"></div><span class="corner c-tl"></span><span class="corner c-tr"></span><span class="corner c-bl"></span><span class="corner c-br"></span>` +
      `<div class="inner">${wordmark}` +
      `<div class="eyebrow">Academy</div>` +
      `<h1>Certificate of Completion</h1>` +
      `<div class="divider"><span class="line"></span><span>&#10022;</span><span class="line r"></span></div>` +
      `<div class="pres">This certificate is proudly presented to</div>` +
      `<div class="name">${who}</div><div class="nrule"></div>` +
      `<div class="body">for successfully completing all <b>${TOTAL} lessons</b> of the <b>Business360 Academy</b>, mastering every module of the platform.</div>` +
      `<div style="margin:12px 0 4px">${seal}</div>` +
      `<div class="foot"><div class="sig"><div class="val">${date}</div><div class="ln"></div><div class="lbl">Date</div></div>` +
      `<div class="sig"><div class="val">Business360 Academy</div><div class="ln"></div><div class="lbl">Issued by &middot; v${ACADEMY_VERSION}</div></div></div>` +
      `</div></div>` +
      `<script>window.onload=function(){setTimeout(function(){window.print();},500);}<\/script></body></html>`,
    );
    w.document.close();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl p-8 text-white shadow-soft-lg bg-[linear-gradient(135deg,#0D9488_0%,#0F766E_55%,#EA580C_120%)]">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 text-white/85 text-callout font-semibold uppercase tracking-wide">
            <GraduationCap className="w-4 h-4" /> Business360 Academy
            <span className="normal-case px-1.5 py-0.5 rounded-md bg-white/20 text-white text-caption font-bold tracking-normal">v{ACADEMY_VERSION}</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold leading-tight tracking-tight">{t("academy.heroTitle", "Learn the whole platform, one lesson at a time")}</h1>
          <p className="mt-2 text-white/85 text-[15px] leading-relaxed">
            {TOTAL} {t("academy.heroBody", "guided video tutorials for every module — recorded from the live product, from your first login to advanced automation.")}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openLesson(resume.code)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-[#0f172a] text-body font-semibold shadow-soft-sm hover:bg-white/90 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <Play className="w-4 h-4 fill-current text-primary" />
              {resuming ? `${t("academy.resume", "Resume")} ${resume.code}` : doneCount >= TOTAL ? t("academy.rewatch", "Rewatch") : doneCount > 0 ? t("academy.continueLearning", "Continue learning") : t("academy.startWatching", "Start watching")}
            </button>
            <div className="flex items-center gap-2.5 text-white/90 text-callout" aria-label={`${doneCount} of ${TOTAL} watched`}>
              <div className="h-2 w-40 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-white rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="tabular-nums font-medium whitespace-nowrap">{doneCount}/{TOTAL} {t("academy.watched", "watched")}</span>
            </div>
            <button
              type="button"
              onClick={() => setCertOpen(true)}
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-white/15 text-white text-callout font-medium hover:bg-white/25 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <Award className="w-4 h-4" /> {doneCount >= TOTAL ? t("academy.getCertificate", "Get certificate") : t("academy.certificate", "Certificate")}
            </button>
          </div>
        </div>
        <GraduationCap className="pointer-events-none absolute -right-6 -bottom-8 w-56 h-56 text-white/10" />
      </div>

      {/* Continue watching */}
      {continueList.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-baseline gap-3">
            <span className="h-5 w-1.5 rounded-full bg-primary" />
            <h2 className="text-[20px] font-semibold text-foreground tracking-tight">{t("academy.continueWatching", "Continue watching")}</h2>
            <span className="hidden sm:inline text-callout text-muted-foreground">{t("academy.pickUp", "Pick up where you left off")}</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {continueList.map((l) => {
              const p = progress[l.code];
              const barPct = p && p.d > 0 ? Math.min(100, Math.round((p.t / p.d) * 100)) : 0;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => openLesson(l.code)}
                  aria-label={`Resume ${l.code} — ${l.name}`}
                  className="group relative shrink-0 w-60 snap-start rounded-xl border border-border bg-card overflow-hidden text-start cursor-pointer transition-shadow hover:shadow-soft-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <div className="relative aspect-video overflow-hidden bg-muted">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={posterUrl(l)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="h-11 w-11 rounded-full bg-white/90 text-primary flex items-center justify-center shadow-soft-md"><Play className="w-5 h-5 ms-0.5 fill-current" /></span>
                    </span>
                    <span className="absolute top-2 start-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-caption font-bold tabular-nums">{l.code}</span>
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30"><div className="h-full bg-primary" style={{ width: `${barPct}%` }} /></div>
                  </div>
                  <div className="p-2.5">
                    <p className="text-callout font-semibold text-foreground truncate">{l.name}</p>
                    <p className="text-caption text-muted-foreground tabular-nums mt-0.5">{barPct}% watched · resume</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Saved */}
      {savedList.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-baseline gap-3">
            <span className="h-5 w-1.5 rounded-full bg-warning" />
            <h2 className="text-[20px] font-semibold text-foreground tracking-tight">{t("academy.saved", "Saved")}</h2>
            <span className="hidden sm:inline text-callout text-muted-foreground">{t("academy.savedSub", "Your bookmarked lessons")}</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {savedList.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => openLesson(l.code)}
                aria-label={`Play ${l.code} — ${l.name}`}
                className="group relative shrink-0 w-60 snap-start rounded-xl border border-border bg-card overflow-hidden text-start cursor-pointer transition-shadow hover:shadow-soft-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <div className="relative aspect-video overflow-hidden bg-muted">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={posterUrl(l)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute inset-0 flex items-center justify-center"><span className="h-11 w-11 rounded-full bg-white/90 text-primary flex items-center justify-center shadow-soft-md"><Play className="w-5 h-5 ms-0.5 fill-current" /></span></span>
                  <span className="absolute top-2 start-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-caption font-bold tabular-nums">{l.code}</span>
                  <span className="absolute top-2 end-2 h-6 w-6 rounded-lg bg-white/90 text-warning flex items-center justify-center"><Bookmark className="w-3.5 h-3.5 fill-current" /></span>
                  {watched.has(l.code) && <span className="absolute bottom-2 start-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success text-white text-caption font-semibold"><Check className="w-3.5 h-3.5" /> Done</span>}
                </div>
                <div className="p-2.5">
                  <p className="text-callout font-semibold text-foreground truncate">{l.name}</p>
                  <p className="text-caption text-muted-foreground truncate mt-0.5">{l.track}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center h-11 px-3.5 rounded-xl border border-border bg-card w-full sm:w-auto sm:flex-1 sm:max-w-md focus-within:ring-2 focus-within:ring-primary/25 transition-shadow">
            <Search className="w-4 h-4 text-muted-foreground shrink-0 me-2.5" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setQuery(""); e.currentTarget.blur(); } }}
              placeholder={`${t("academy.searchLessons", "Search lessons")}…`}
              aria-label={t("academy.searchLessons", "Search lessons")}
              className="bg-transparent border-0 outline-none text-body text-foreground placeholder:text-muted-foreground w-full"
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")} aria-label={t("academy.clearSearch", "Clear search")} className="ms-1.5 shrink-0 h-6 w-6 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                <X className="w-3.5 h-3.5" />
              </button>
            ) : (
              <kbd className="ms-1.5 shrink-0 hidden sm:inline-flex items-center h-5 px-1.5 rounded border border-border text-caption text-muted-foreground" aria-hidden>/</kbd>
            )}
          </div>
          <div className="flex items-center gap-1.5" role="group" aria-label={t("academy.filterByLevel", "Filter by level")}>
            {LEVELS.map((lv) => (
              <button
                key={lv}
                type="button"
                onClick={() => setLevel(lv)}
                aria-pressed={level === lv}
                className={cn(
                  "h-9 px-3 rounded-lg text-callout font-medium transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  level === lv ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {levelLabel(lv)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSavedOnly((v) => !v)}
            aria-pressed={savedOnly}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-callout font-medium transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              savedOnly ? "bg-warning text-white" : "border border-border text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <Bookmark className={cn("w-4 h-4", savedOnly && "fill-current")} /> {t("academy.saved", "Saved")}{saved.size > 0 ? ` (${saved.size})` : ""}
          </button>
          <span className="text-callout text-muted-foreground whitespace-nowrap ms-auto tabular-nums">{shownCount} {t("academy.of", "of")} {TOTAL}</span>
        </div>
        {/* Track chips */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" role="group" aria-label={t("academy.filterByTrack", "Filter by track")}>
          {["All", ...TRACK_TITLES].map((tt) => (
            <button
              key={tt}
              type="button"
              onClick={() => setTrackFilter(tt)}
              aria-pressed={trackFilter === tt}
              className={cn(
                "h-8 px-3 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                trackFilter === tt ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {tt === "All" ? levelLabel("All") : trackTitle(tt)}
            </button>
          ))}
        </div>
      </div>

      {/* Tracks */}
      {tracks.map((track) => {
        const stat = trackStats[track.title];
        return (
          <section key={track.title} className="space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="h-5 w-1.5 rounded-full bg-primary" />
              <h2 className="text-[20px] font-semibold text-foreground tracking-tight">{trackTitle(track.title)}</h2>
              <span className="hidden sm:inline text-callout text-muted-foreground">{trackSub(track.title, track.subtitle)}</span>
              <span className="ms-auto text-footnote text-muted-foreground tabular-nums shrink-0">{stat.done}/{stat.total} done</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {track.lessons.map((l) => {
                const chs = l.chapters;
                const chDone = chs ? chs.filter((c) => watched.has(c.code)).length : 0;
                const isDone = chs ? chs.length > 0 && chDone === chs.length : watched.has(l.code);
                const p = progress[l.code];
                const barPct = chs && chs.length
                  ? Math.round((chDone / chs.length) * 100)
                  : (!isDone && p && p.d > 0 ? Math.min(100, Math.round((p.t / p.d) * 100)) : 0);
                return (
                  <article
                    key={l.code}
                    role="button"
                    tabIndex={0}
                    aria-label={`Play ${l.code} — ${l.name}`}
                    onClick={() => openLesson(l.code)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLesson(l.code); } }}
                    className="group relative rounded-xl border border-border bg-card overflow-hidden cursor-pointer transition-shadow duration-200 hover:shadow-soft-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div className="relative aspect-video overflow-hidden bg-muted">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={posterUrl(l)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="h-12 w-12 rounded-full bg-white/90 text-primary flex items-center justify-center shadow-soft-md transition-transform duration-200 motion-safe:group-hover:scale-110">
                          <Play className="w-5 h-5 ms-0.5 fill-current" />
                        </span>
                      </div>
                      <span className="absolute top-2 start-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-caption font-bold tabular-nums">{l.code}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSaved(l.code); }}
                        aria-label={saved.has(l.code) ? `Remove ${l.name} from saved` : `Save ${l.name}`}
                        aria-pressed={saved.has(l.code)}
                        className={cn(
                          "absolute top-2 end-2 h-7 w-7 rounded-lg flex items-center justify-center transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                          saved.has(l.code) ? "bg-white/90 text-warning" : "bg-black/45 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                        )}
                      >
                        <Bookmark className={cn("w-4 h-4", saved.has(l.code) && "fill-current")} />
                      </button>
                      <span className="absolute bottom-2 end-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-caption font-medium tabular-nums">
                        <Clock className="w-3 h-3" /> {l.len}
                      </span>
                      {isDone && (
                        <span className="absolute bottom-2 start-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success text-white text-caption font-semibold shadow-soft-xs" title="Watched">
                          <Check className="w-3.5 h-3.5" /> Done
                        </span>
                      )}
                      {chs && chs.length > 0 && !isDone && (
                        <span className="absolute bottom-2 start-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-caption font-semibold shadow-soft-xs">
                          <Play className="w-3 h-3 fill-current" /> {chDone > 0 ? `${chDone}/${chs.length}` : `${chs.length} videos`}
                        </span>
                      )}
                      {barPct > 0 && (
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30">
                          <div className="h-full bg-primary" style={{ width: `${barPct}%` }} />
                        </div>
                      )}
                    </div>
                    <div className="p-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-body font-semibold text-foreground truncate">{l.name}</h3>
                        <span className={cn("shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full", LEVEL_TONE[l.level])}>{levelLabel(l.level)}</span>
                      </div>
                      <p className="mt-1 text-footnote text-muted-foreground leading-relaxed line-clamp-2">{l.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}

      {tracks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
          <BookOpen className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-[15px] font-medium text-foreground">{t("academy.noMatch", "No lessons match your filters")}</p>
          <p className="text-callout">Try a module name like “inventory” or clear the filters.</p>
        </div>
      )}

      {/* Modal player */}
      {active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-label={`${active.name} tutorial`}>
          <button className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default" aria-label={t("academy.closePlayer", "Close player")} onClick={close} />
          <div className="relative z-10 w-full max-w-4xl rounded-2xl bg-card border border-border shadow-soft-lg overflow-hidden anim-slide-down">
            <div className="relative bg-black aspect-video">
              <video
                ref={videoRef}
                key={active.code}
                src={videoUrl(active)}
                poster={posterUrl(active)}
                controls
                autoPlay
                playsInline
                className="w-full h-full"
                onLoadedMetadata={onLoaded}
                onTimeUpdate={onTime}
                onPause={onPause}
                onEnded={onEnded}
              />
              <button
                ref={closeRef}
                type="button"
                onClick={close}
                aria-label="Close"
                className="absolute top-2 end-2 h-9 w-9 rounded-lg bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-center gap-2 text-footnote text-muted-foreground flex-wrap">
                <span className="font-bold text-primary tabular-nums">{active.code}</span>
                <span className={cn("px-2 py-0.5 rounded-full text-caption font-semibold", LEVEL_TONE[active.level])}>{levelLabel(active.level)}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {active.len}</span>
                <span className="truncate">· {active.track}</span>
                <span className="tabular-nums">· {(activeIdx ?? 0) + 1} / {TOTAL}</span>
              </div>
              <h3 className="mt-1.5 text-[18px] font-semibold text-foreground leading-tight">{active.name}</h3>
              <p className="mt-1 text-callout text-muted-foreground leading-relaxed">{active.desc}</p>

              {activeIdx != null && activeIdx < FLAT.length - 1 && (
                <button
                  type="button"
                  onClick={() => go(1)}
                  className="mt-4 w-full flex items-center gap-3 rounded-xl border border-border bg-muted/40 hover:bg-muted p-2.5 text-start transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <div className="relative w-24 shrink-0 aspect-video rounded-lg overflow-hidden bg-muted">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={posterUrl(FLAT[activeIdx + 1])} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center"><Play className="w-4 h-4 text-white fill-current drop-shadow" /></span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-caption uppercase tracking-wide text-muted-foreground font-semibold">{t("academy.upNext", "Up next")}</p>
                    <p className="text-callout font-semibold text-foreground truncate">{FLAT[activeIdx + 1].code} · {FLAT[activeIdx + 1].name}</p>
                    <p className="text-caption text-muted-foreground truncate">{FLAT[activeIdx + 1].desc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground ms-auto shrink-0" />
                </button>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleWatched(active.code)}
                    className={cn(
                      "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-callout font-medium border transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                      watched.has(active.code) ? "border-success/40 text-success bg-success/10" : "border-border text-foreground hover:bg-muted",
                    )}
                  >
                    <Check className="w-4 h-4" /> {watched.has(active.code) ? "Watched" : "Mark as watched"}
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-callout font-medium border border-border text-foreground hover:bg-muted transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <Link2 className="w-4 h-4" /> {copied ? t("academy.copied", "Copied!") : t("academy.copyLink", "Copy link")}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    disabled={activeIdx === 0}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-callout font-medium border border-border text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => go(1)}
                    disabled={activeIdx === FLAT.length - 1}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-callout font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course (multi-chapter) player */}
      {course && courseL && activeCh && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-label={`${courseL.name} course`}>
          <button className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default" aria-label="Close" onClick={close} />
          <div className="relative z-10 w-full max-w-5xl rounded-2xl bg-card border border-border shadow-soft-lg overflow-hidden anim-slide-down grid grid-cols-1 md:grid-cols-[1fr_300px]">
            <div className="min-w-0">
              <div className="relative bg-black aspect-video">
                <video
                  ref={videoRef}
                  key={activeCh.code}
                  src={`/tutorials/${activeCh.code}.mp4`}
                  poster={`/tutorials/thumbs/${courseL.code}.jpg`}
                  controls autoPlay playsInline className="w-full h-full"
                  onLoadedMetadata={() => { const v = videoRef.current, p = progress[activeCh.code]; if (v && p && p.d > 0 && p.t > 3 && p.t < p.d * 0.95) { try { v.currentTime = p.t; } catch { /* ignore */ } } }}
                  onTimeUpdate={() => { const v = videoRef.current; if (!v || !v.duration) return; const now = Date.now(); if (now - lastSaveRef.current > 4000) { lastSaveRef.current = now; saveProgress(activeCh.code, v.currentTime, v.duration); } }}
                  onPause={() => { const v = videoRef.current; if (v && v.duration && !v.ended) saveProgress(activeCh.code, v.currentTime, v.duration); }}
                  onEnded={() => { markWatched(activeCh.code); clearProgress(activeCh.code); if (course.ch < courseChs.length - 1) goCh(1); }}
                />
                <button ref={closeRef} type="button" onClick={close} aria-label="Close" className="absolute top-2 end-2 h-9 w-9 rounded-lg bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2 text-footnote text-muted-foreground flex-wrap">
                  <span className="font-bold text-primary">{courseL.code} · {courseL.name}</span>
                  <span className={cn("px-2 py-0.5 rounded-full text-caption font-semibold", LEVEL_TONE[courseL.level])}>{levelLabel(courseL.level)}</span>
                  <span className="tabular-nums">· Chapter {course.ch + 1} of {courseChs.length}</span>
                </div>
                <h3 className="mt-1.5 text-[18px] font-semibold text-foreground leading-tight">{activeCh.name}</h3>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <button type="button" onClick={() => toggleWatched(activeCh.code)} className={cn("inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-callout font-medium border transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", watched.has(activeCh.code) ? "border-success/40 text-success bg-success/10" : "border-border text-foreground hover:bg-muted")}><Check className="w-4 h-4" /> {watched.has(activeCh.code) ? t("academy.watchedBtn", "Watched") : t("academy.markWatched", "Mark watched")}</button>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => goCh(-1)} disabled={course.ch === 0} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-callout font-medium border border-border text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /> Prev</button>
                    <button type="button" onClick={() => goCh(1)} disabled={course.ch === courseChs.length - 1} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-callout font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">{t("academy.next", "Next")} <ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t md:border-t-0 md:border-s border-border bg-muted/20 flex flex-col max-h-[38vh] md:max-h-[82vh]">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
                <span className="text-callout font-semibold text-foreground">{t("academy.chapters", "Chapters")}</span>
                <span className="text-footnote text-muted-foreground tabular-nums">{courseChs.filter((c) => watched.has(c.code)).length}/{courseChs.length} done</span>
              </div>
              <div className="overflow-y-auto p-2 space-y-1">
                {courseChs.map((c, i) => {
                  const done = watched.has(c.code); const cur = i === course.ch;
                  return (
                    <button key={c.code} type="button" onClick={() => setCourse({ lessonCode: courseL.code, ch: i })} className={cn("w-full flex items-center gap-2.5 p-2 rounded-lg text-start transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", cur ? "bg-primary/10" : "hover:bg-muted")}>
                      <span className={cn("shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-caption font-bold", done ? "bg-success text-white" : cur ? "bg-primary text-primary-foreground" : "bg-muted-foreground/15 text-muted-foreground")}>{done ? <Check className="w-3.5 h-3.5" /> : i + 1}</span>
                      <span className="min-w-0 flex-1"><span className={cn("block text-callout truncate", cur ? "font-semibold text-foreground" : "text-foreground/90")}>{c.name}</span><span className="block text-caption text-muted-foreground tabular-nums">{c.len}</span></span>
                      {cur && <Play className="w-3.5 h-3.5 text-primary shrink-0 fill-current" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certificate */}
      {certOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t("academy.certDialog", "Completion certificate")}>
          <button className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default" aria-label="Close" onClick={() => setCertOpen(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-2xl bg-card border border-border shadow-soft-lg overflow-hidden anim-slide-down">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h3 className="text-title-3 font-semibold text-foreground inline-flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> Certificate of Completion</h3>
              <button onClick={() => setCertOpen(false)} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-xl border-2 border-warning/40 bg-gradient-to-b from-warning/[0.06] to-transparent p-6 text-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/logo-mark.svg" alt="Business360" className="mx-auto h-12 w-12" />
                <p className="mt-3 text-footnote uppercase tracking-[0.2em] text-muted-foreground font-semibold">Business360 Academy</p>
                <p className="mt-2 text-callout text-muted-foreground">{t("academy.certifies", "This certifies that")}</p>
                <p className="mt-1 text-[20px] font-bold text-foreground">{name || "Your Name"}</p>
                <p className="mt-2 text-callout text-muted-foreground">
                  {doneCount >= TOTAL ? `has completed all ${TOTAL} lessons of the Business360 Academy.` : `is completing the Business360 Academy — ${doneCount} of ${TOTAL} lessons done.`}
                </p>
                {doneCount < TOTAL && (
                  <div className="mt-3">
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
                    <p className="mt-1.5 text-footnote text-muted-foreground">{TOTAL - doneCount} lesson{TOTAL - doneCount === 1 ? "" : "s"} to go to unlock your certificate.</p>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="cert-name" className="block text-footnote font-medium text-muted-foreground mb-1">{t("academy.nameOnCert", "Name on the certificate")}</label>
                <input
                  id="cert-name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); try { localStorage.setItem("b360:academy:name", e.target.value); } catch { /* ignore */ } }}
                  placeholder="Your Name"
                  className="w-full h-10 px-3 rounded-lg border border-border bg-card text-body text-foreground outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCertOpen(false)} className="h-9 px-3 rounded-lg text-callout font-medium border border-border text-foreground hover:bg-muted transition-colors cursor-pointer">{t("academy.close", "Close")}</button>
                <button
                  type="button"
                  onClick={() => void printCertificate()}
                  disabled={doneCount < TOTAL}
                  title={doneCount < TOTAL ? "Complete all lessons to unlock" : "Print or save as PDF"}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-callout font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Award className="w-4 h-4" /> Print / Save PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
